import { Fragment, useMemo, useState } from "react";
import { CalendarDays, AlertTriangle } from "lucide-react";
import {
  format,
  addDays,
  addWeeks,
  addMonths,
  isWeekend,
  startOfDay,
  differenceInCalendarDays,
} from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Calendar } from "../ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { SectionHead } from "./shared";

// =========================================================================
// TIMELINES
// =========================================================================
const durationLabel = (assumed) =>
  `${assumed.value} ${
    assumed.unit === "months"
      ? assumed.value === 1
        ? "month"
        : "months"
      : assumed.value === 1
        ? "week"
        : "weeks"
  }`;

// "5 weeks", "1 month", "41 working days", for phase-duration rows
// and unit totals ({unit, value}).
const phaseDurText = (pd) => {
  const v = pd.value;
  const unit =
    pd.unit === "working days"
      ? v === 1
        ? "working day"
        : "working days"
      : pd.unit === "months"
        ? v === 1
          ? "month"
          : "months"
        : v === 1
          ? "week"
          : "weeks";
  return `${v} ${unit}`;
};

function PhaseDurationsTable({ durations, totals }) {
  return (
    <div
      className="bg-white border border-stone-200 rounded-xl overflow-hidden"
      data-testid="phase-durations-table"
    >
      <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/60">
        <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-stone-500">
          Timeline by phase
        </p>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {durations.map((pd, i) => (
            <tr
              key={i}
              className="border-t border-stone-100 first:border-t-0"
              data-testid={`phase-duration-row-${i}`}
            >
              <td className="px-5 py-2.5 text-stone-800">
                {pd.phase_name || `Phase ${i + 1}`}
              </td>
              <td className="px-5 py-2.5 text-right text-stone-900 font-medium whitespace-nowrap">
                {phaseDurText(pd)}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-stone-300 bg-stone-50 font-medium">
            <td className="px-5 py-2.5 text-stone-900">Total</td>
            <td
              className="px-5 py-2.5 text-right text-stone-900 whitespace-nowrap"
              data-testid="phase-durations-total"
            >
              {totals.map((t) => phaseDurText(t)).join(" + ")}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function TimelinesTab({ d, narrativeLoading, samplesIncreased, acknowledged, setAcknowledged, recordEvent = () => {} }) {
  const timelines = d.timelines || [];
  const assumed = d.assumed_duration || null;
  const phaseDurations = d.phase_durations || [];
  const hasAny =
    timelines.length > 0 || phaseDurations.length > 0 || !!assumed;

  const [mode, setMode] = useState("sequential");

  // ---- public-holiday awareness: holidays for every fieldwork
  // country are ALWAYS applied, clients can no longer opt out. The
  // schedule pauses on any day that's a holiday in any market the
  // proposal touches.
  const publicHolidays = d.public_holidays || {};
  const holidayCountries = Object.keys(publicHolidays);
  const holidaySet = useMemo(() => {
    const m = new Map();
    holidayCountries.forEach((c) => {
      (publicHolidays[c] || []).forEach((h) => {
        if (!m.has(h.date)) m.set(h.date, []);
        m.get(h.date).push({ country: c, name: h.name });
      });
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.public_holidays]);

  // Merge fieldwork countries (from the cost pivots) with countries
  // that have bank-holiday data, both sets need a start-date picker.
  // With 2+ countries the Gantt renders one row per country per
  // working-day phase, each using its OWN holiday calendar AND its
  // OWN kick-off date, so markets can start and finish on different
  // real dates.
  const allCountries = useMemo(() => {
    const out = [];
    (d.phase_pivots || []).forEach((p) =>
      (p.countries || []).forEach((c) => {
        if (c && !out.includes(c)) out.push(c);
      })
    );
    Object.keys(d.public_holidays || {}).forEach((c) => {
      if (c && !out.includes(c)) out.push(c);
    });
    return out;
  }, [d.phase_pivots, d.public_holidays]);

  // Always at least one entry so proposals quoting no countries still
  // surface a "Project start" picker.
  const effectiveCountries = useMemo(
    () => (allCountries.length ? allCountries : ["__default__"]),
    [allCountries]
  );
  const firstCountry = effectiveCountries[0];

  // Per-country start dates. The first country is the anchor: changing
  // it cascades to every other market so the whole project shifts in
  // one click. Editing any other country pins JUST that market, its
  // start no longer follows the anchor.
  const [countryStarts, setCountryStarts] = useState(() => {
    const def = defaultKickoff();
    const m = new Map();
    effectiveCountries.forEach((c) => m.set(c, def));
    return m;
  });
  const setCountryStart = (country, date) => {
    setCountryStarts((prev) => {
      const next = new Map(prev);
      if (country === firstCountry) {
        effectiveCountries.forEach((c) => next.set(c, date));
      } else {
        next.set(country, date);
      }
      return next;
    });
  };

  const countrySets = useMemo(() => {
    if (!allCountries.length) return null;
    return allCountries.map((c) => {
      const m = new Map();
      (publicHolidays[c] || []).forEach((h) => {
        m.set(h.date, [{ country: c, name: h.name }]);
      });
      return { country: c, set: m };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCountries, d.public_holidays]);

  const schedule = useMemo(
    () =>
      buildSchedule(
        d.phase_durations || [],
        d.timelines || [],
        d.assumed_duration || null,
        countryStarts,
        effectiveCountries,
        mode,
        holidaySet,
        countrySets,
        d.translation_countries || null
      ),
    [d.phase_durations, d.timelines, d.assumed_duration, d.translation_countries, countryStarts, effectiveCountries, mode, holidaySet, countrySets]
  );
  const multiPhase = (schedule?.rows?.length || 0) > 1;

  // Planner "Start by country" dropdown: "__all__" shifts every
  // market together; a specific country overrides just that one.
  const [selectedStartCountry, setSelectedStartCountry] = useState("__all__");

  // When a single country is picked in the "Start by country"
  // dropdown, the Gantt / completion card / projection table show
  // just that market's rows with kickoff + completion recomputed
  // for it. "__all__" passes the full schedule straight through.
  const displaySchedule = useMemo(() => {
    if (!schedule || selectedStartCountry === "__all__") return schedule;
    const rows = schedule.rows
      .map((r) => ({
        ...r,
        countryRows: (r.countryRows || []).filter(
          (cr) => cr.country === selectedStartCountry
        ),
      }))
      .filter((r) => r.countryRows.length > 0);
    if (!rows.length) return schedule;
    let kickoff = null;
    let completion = null;
    rows.forEach((r) =>
      r.countryRows.forEach((cr) => {
        if (!kickoff || cr.start < kickoff) kickoff = cr.start;
        if (!completion || cr.end > completion) completion = cr.end;
      })
    );
    return {
      rows,
      kickoff,
      completion,
      axisStart: addDays(kickoff, -2),
      axisEnd: addDays(completion, 3),
    };
  }, [schedule, selectedStartCountry]);

  // When a single country is picked, the Gantt's holiday dots and
  // bands show ONLY that market's bank holidays; "__all__" keeps the
  // merged every-country calendar.
  const displayHolidaySet = useMemo(() => {
    if (selectedStartCountry === "__all__" || !countrySets) return holidaySet;
    const cs = countrySets.find((c) => c.country === selectedStartCountry);
    return cs ? cs.set : holidaySet;
  }, [selectedStartCountry, countrySets, holidaySet]);

  // ---- Tab-open disclaimer ----
  // Timelines are estimates, not binding commitments, clients must
  // acknowledge that once PER PAGE VISIT. The dialog auto-fires on
  // tab open (same pattern as the Costs tab) and can only be closed
  // via "I understand". `acknowledged` is held by PublicProposal at
  // the page level so tab switches within a single visit don't
  // re-prompt, but every fresh page load resets the parent flag and
  // re-shows the disclaimer (deliberate per-visit warning).
  const [disclaimer, setDisclaimer] = useState(() =>
    !acknowledged ? { type: "tab_open" } : null
  );
  const confirmDisclaimer = () => {
    setAcknowledged(true);
    setDisclaimer(null);
  };

  // Direct (non-cascading) setter, used by the "Start by country"
  // dropdown when a single country is selected so the anchor's date
  // doesn't override the others.
  const setCountryStartDirect = (country, date) => {
    setCountryStarts((prev) => {
      const next = new Map(prev);
      next.set(country, date);
      return next;
    });
  };

  if (!hasAny) {
    return (
      <div className="space-y-10" data-testid="timelines-tab">
        <SectionHead n="04" title="Timelines" />
        <p className="text-sm text-stone-500">
          {narrativeLoading
            ? "Loading timeline…"
            : "No phase-by-phase timeline available."}
        </p>
      </div>
    );
  }

  // Tab-open acknowledgment dialog. Cannot be dismissed via Esc /
  // overlay while unacknowledged - "I understand" is the only way in.
  const dialog = (
    <AlertDialog
      open={disclaimer != null}
      onOpenChange={(open) => {
        if (!open) {
          if (disclaimer?.type === "tab_open" && !acknowledged) return;
          setDisclaimer(null);
        }
      }}
    >
      <AlertDialogContent data-testid="timelines-disclaimer-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>About these timelines</AlertDialogTitle>
          <AlertDialogDescription>
            Timelines shown are indicative estimates based on assumed
            recruitment feasibility. Fieldwork dates cannot be
            guaranteed until we have received the final screeners,
            discussion guides, stimulus materials, and confirmation
            of any required ethics or advisory board approvals.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={confirmDisclaimer}
            data-testid="timelines-disclaimer-understand"
            className="bg-[#1B4F8A] hover:bg-[#173f6e]"
          >
            I understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <div className="space-y-10" data-testid="timelines-tab">
      {dialog}
      <SectionHead n="04" title="Timelines" />

      {samplesIncreased && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="note"
          data-testid="timeline-scope-note"
        >
          <AlertTriangle
            className="w-4 h-4 mt-0.5 shrink-0 text-amber-600"
            aria-hidden
          />
          <p>
            <strong>You&apos;ve increased sample sizes in the cost view.</strong>{" "}
            The schedule below reflects the quoted scope: larger samples
            typically extend recruitment and fieldwork, so timelines are
            likely to be affected. Please check with the Medicys team.
          </p>
        </div>
      )}

      {/* ---- interactive project planner ---- */}
      <div
        className="bg-white border border-stone-200 rounded-xl overflow-hidden"
        data-testid="timeline-planner"
      >
        <div className="bg-[#0B1220] px-4 sm:px-6 py-4 flex items-center gap-3">
          <CalendarDays className="w-4 h-4 text-[#3EA8DC] shrink-0" />
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-stone-300 font-medium">
              Project planner
            </p>
            <p className="text-stone-100 font-medium">
              Pick the date our quoted activities will start
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What counts as the start date?"
                      data-testid="planner-start-info"
                      className="inline-flex items-center justify-center align-middle mx-1.5 w-4 h-4 rounded-full bg-[#3EA8DC]/20 text-[#3EA8DC] hover:bg-[#3EA8DC]/35 transition-colors cursor-help"
                    >
                      <span className="text-[10px] font-bold leading-none">!</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    Typically the date Medicys receives the final and
                    approved screener and ICF.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              and we&apos;ll map the whole study onto real dates.
            </p>
          </div>
        </div>
        <div className="p-4 sm:p-6 space-y-6">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4 no-print">
            <div className="flex flex-col gap-2 min-w-0">
              <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500">
                Start by country
              </p>
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="country-start-pickers"
              >
                {effectiveCountries.length > 1 && (
                  <select
                    value={selectedStartCountry}
                    onChange={(e) => {
                      const c = e.target.value;
                      setSelectedStartCountry(c);
                      if (c !== "__all__") {
                        recordEvent("timeline_country_select", { meta: { country: c } });
                      }
                    }}
                    data-testid="country-start-select"
                    className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:border-[#3EA8DC] focus:outline-none focus:border-[#1B4F8A] cursor-pointer transition-colors"
                  >
                    <option value="__all__">All countries</option>
                    {effectiveCountries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
                {effectiveCountries.length > 1 ? (
                  selectedStartCountry === "__all__" ? (
                    <CountryStartPicker
                      country="__all__"
                      label="All countries"
                      date={countryStarts.get(firstCountry) || defaultKickoff()}
                      onChange={(dt) => setCountryStart(firstCountry, dt)}
                      isAnchor
                    />
                  ) : (
                    <CountryStartPicker
                      country={selectedStartCountry}
                      label={selectedStartCountry}
                      date={
                        countryStarts.get(selectedStartCountry) ||
                        defaultKickoff()
                      }
                      onChange={(dt) =>
                        setCountryStartDirect(selectedStartCountry, dt)
                      }
                    />
                  )
                ) : (
                  <CountryStartPicker
                    country={firstCountry}
                    label={
                      firstCountry === "__default__"
                        ? "Project start"
                        : firstCountry
                    }
                    date={countryStarts.get(firstCountry) || defaultKickoff()}
                    onChange={(dt) => setCountryStart(firstCountry, dt)}
                  />
                )}
              </div>
              {effectiveCountries.length > 1 && (
                <p className="text-[10px] text-stone-400 italic max-w-xl">
                  {selectedStartCountry === "__all__" ? (
                    <>
                      Pick a date to shift every market together, or select
                      a single country to override just that one.
                    </>
                  ) : (
                    <>
                      Editing{" "}
                      <span className="font-medium not-italic text-stone-500">
                        {selectedStartCountry}
                      </span>{" "}
                      overrides just that market. Switch back to{" "}
                      <span className="font-medium not-italic text-stone-500">
                        All countries
                      </span>{" "}
                      to shift everything together.
                    </>
                  )}
                </p>
              )}
            </div>

            {multiPhase && (
              <div className="inline-flex bg-stone-200/60 rounded-lg p-1 text-sm">
                <button
                  data-testid="timeline-mode-sequential"
                  onClick={() => setMode("sequential")}
                  className={`px-3 py-1.5 rounded-md transition-colors ${
                    mode === "sequential"
                      ? "bg-white shadow-sm text-stone-900 font-medium"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  Phases in sequence
                </button>
                <button
                  data-testid="timeline-mode-parallel"
                  onClick={() => setMode("parallel")}
                  className={`px-3 py-1.5 rounded-md transition-colors ${
                    mode === "parallel"
                      ? "bg-white shadow-sm text-stone-900 font-medium"
                      : "text-stone-600 hover:text-stone-900"
                  }`}
                >
                  Phases in parallel
                </button>
              </div>
            )}
          </div>

          {displaySchedule && (
            <GanttChart schedule={displaySchedule} holidaySet={displayHolidaySet} />
          )}

          {displaySchedule && (
            <div
              className="bg-stone-50 border border-stone-200 rounded-lg p-5 flex items-end justify-between gap-4 flex-wrap"
              data-testid="timeline-completion"
            >
              <div>
                <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1">
                  Projected delivery
                  {selectedStartCountry !== "__all__" &&
                    ` · ${selectedStartCountry} only`}
                  {multiPhase &&
                    ` · phases in ${
                      mode === "sequential" ? "sequence" : "parallel"
                    }`}
                </p>
                <p
                  className="text-2xl font-serif font-light text-stone-900"
                  data-testid="timeline-completion-date"
                >
                  {format(displaySchedule.completion, "EEEE d MMMM yyyy")}
                </p>
              </div>
              <p className="text-xs text-stone-500">
                {effectiveCountries.length > 1 &&
                allCountries.length > 1 &&
                selectedStartCountry === "__all__"
                  ? `Earliest start ${fmtDay(displaySchedule.kickoff)}`
                  : `Kick-off ${fmtDay(displaySchedule.kickoff)}`}
              </p>
            </div>
          )}

        </div>
      </div>

      {/* printable per-phase × per-country dates. Single-phase
          proposals drop the Phase column - the name is redundant. */}
      {schedule?.rows?.length > 0 && (
        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="timeline-projection-table">
            <thead>
              <tr className="bg-stone-100/60 text-left">
                {displaySchedule.rows.length > 1 && (
                  <th className="px-4 py-2.5 font-medium text-stone-700">Phase</th>
                )}
                <th className="px-4 py-2.5 font-medium text-stone-700">Country</th>
                <th className="px-4 py-2.5 font-medium text-stone-700">
                  Projected start
                </th>
                <th className="px-4 py-2.5 font-medium text-stone-700">
                  Projected finish
                </th>
              </tr>
            </thead>
            <tbody>
              {displaySchedule.rows.flatMap((r, i) =>
                (r.countryRows || []).map((cr, ci) => (
                  <tr
                    key={`${i}-${ci}`}
                    className={
                      "border-t border-stone-100 " +
                      (ci === 0 && i > 0 ? "border-t-stone-200" : "")
                    }
                    data-testid={`timeline-row-${i}-${ci}`}
                  >
                    {displaySchedule.rows.length > 1 && (
                      <td className="px-4 py-2.5 text-stone-800">
                        {ci === 0 ? r.name || `Phase ${i + 1}` : ""}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-stone-700 whitespace-nowrap">
                      {cr.country || "-"}
                    </td>
                    <td className="px-4 py-2.5 text-stone-700">{fmtDay(cr.start)}</td>
                    <td className="px-4 py-2.5 text-stone-700">{fmtDay(cr.end)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {phaseDurations.length >= 2 && (
        <PhaseDurationsTable
          durations={phaseDurations}
          totals={d.phase_durations_total || []}
        />
      )}
    </div>
  );
}

// ---- working-day date maths (Mon–Fri, minus selected public holidays
// and the annual Medicys office closure) ----
const isoDay = (dt) => format(dt, "yyyy-MM-dd");
// Medicys (UK) closes from lunchtime on 24 December until the first
// working day of the new year: 25–31 Dec are fully closed, 24 Dec
// counts as a half working day, and 1 Jan (or its UK substitute Monday
// when New Year's Day lands on a weekend) is closed too.
const isOfficeClosed = (dt) => {
  const m = dt.getMonth();
  const d = dt.getDate();
  if (m === 11 && d >= 25) return true; // 25–31 Dec
  if (m === 0) {
    if (d === 1) return true;
    const dow = new Date(dt.getFullYear(), 0, 1).getDay();
    if (dow === 6 && d === 3) return true; // 1 Jan Sat → Mon 3 Jan
    if (dow === 0 && d === 2) return true; // 1 Jan Sun → Mon 2 Jan
  }
  return false;
};
const isXmasEveHalfDay = (dt) => dt.getMonth() === 11 && dt.getDate() === 24;
// A day is non-working when it's a weekend, falls inside the office
// closure, OR is a public holiday in any selected country (holidaySet:
// Map isoDate -> [{country, name}]).
const isNonWorking = (dt, holidaySet) =>
  isWeekend(dt) || isOfficeClosed(dt) || !!holidaySet?.has(isoDay(dt));
// How much of a working day `dt` contributes (0.5 on Christmas Eve).
const workingDayValue = (dt, holidaySet) =>
  isNonWorking(dt, holidaySet) ? 0 : isXmasEveHalfDay(dt) ? 0.5 : 1;
const nextBusinessDay = (date, holidaySet) => {
  let x = date;
  while (isNonWorking(x, holidaySet)) x = addDays(x, 1);
  return x;
};
// Advance `n` working days, counting the (business-day) start as day 1.
const addWorkingDays = (start, n, holidaySet) => {
  let x = nextBusinessDay(start, holidaySet);
  let remaining = Math.max(0, n - workingDayValue(x, holidaySet));
  while (remaining > 1e-9) {
    x = addDays(x, 1);
    remaining -= workingDayValue(x, holidaySet);
  }
  return x;
};
const fmtDay = (dt) => format(dt, "EEE d MMM yyyy");

// Default kick-off: the next Monday.
const defaultKickoff = () => {
  let x = startOfDay(addDays(new Date(), 1));
  while (x.getDay() !== 1) x = addDays(x, 1);
  return x;
};

// What actually happens in each canonical stage, shown in the Gantt
// segment popovers.
const STAGE_DETAILS = {
  Setup:
    "Project kick-off: screener and materials finalised, compliance checks completed and the project team briefed.",
  Programming:
    "Survey scripting, device testing and link checks ahead of launch.",
  Translations:
    "Materials translated into the local languages and quality-checked by native speakers.",
  Recruitment:
    "Panel outreach, screening and scheduling of qualified respondents in each market.",
  Fieldwork:
    "Interviews and sessions conducted and monitored, with quality checks throughout.",
  Moderation:
    "Sessions led by senior healthcare-experienced moderators.",
  Deliverables:
    "Transcripts, recordings, data files and agreed outputs prepared, checked and delivered.",
};

// Build a dated schedule from the quoted phase durations: working-day
// phases use Mon–Fri maths minus the selected countries' public
// holidays (with stage-level dates where the proposal quotes them);
// week/month phases use calendar time. Phases either chain
// (sequential) or all start at kick-off (parallel). Per-country start
// dates shift each market's whole chain independently, anchor
// country drives the overall axis.
function buildSchedule(
  phaseDurations,
  timelines,
  assumed,
  countryStarts,
  effectiveCountries,
  mode,
  holidaySet,
  countrySets,
  translationCountries
) {
  if (!effectiveCountries?.length) return null;
  const tlByName = {};
  timelines.forEach((t) => {
    tlByName[t.phase_name || ""] = t;
  });

  // Each country's start: snap to its first working day under its own
  // holiday calendar (or the global one if the country isn't broken
  // out in the pivot).
  const setOf = (country) => {
    const cs = (countrySets || []).find((x) => x.country === country);
    return cs ? cs.set : holidaySet;
  };
  const snappedStart = (country) =>
    nextBusinessDay(
      startOfDay(countryStarts?.get(country) || defaultKickoff()),
      setOf(country)
    );

  // Anchor = first country. Earliest country start drives the axis.
  const anchorCountry = effectiveCountries[0];
  const anchorStart = snappedStart(anchorCountry);
  let earliestStart = anchorStart;
  effectiveCountries.forEach((c) => {
    const s = snappedStart(c);
    if (s < earliestStart) earliestStart = s;
  });

  // Each country has its own running cursor for sequential chaining
  // through ITS OWN holiday calendar, no parent cursor any more,
  // because there is no parent track in the rendered Gantt.
  const countryCursor = new Map();
  effectiveCountries.forEach((c) => countryCursor.set(c, snappedStart(c)));

  const durations = phaseDurations.length
    ? phaseDurations
    : timelines.map((t) => ({
        phase_name: t.phase_name,
        unit: "working days",
        value: t.total || 1,
      }));
  let rows = [];
  for (const pd of durations) {
    const tl = tlByName[pd.phase_name || ""];
    const countryRows = effectiveCountries.map((country) => {
      const set =
        (countrySets || []).find((x) => x.country === country)?.set ||
        holidaySet;
      const cKickoff = snappedStart(country);
      const cStart =
        mode === "sequential" ? countryCursor.get(country) : cKickoff;
      let cEnd;
      let cStages = [];
      if (pd.unit === "working days") {
        // If the backend flagged which countries actually need
        // translation timing (English-native markets typically don't)
        // drop the Translations stage for the unflagged ones and
        // shorten the country's phase bar accordingly so the timeline
        // reflects the real workflow rather than a boilerplate gap.
        let stagesSrc = tl?.stages || [];
        let droppedDays = 0;
        if (
          Array.isArray(translationCountries) &&
          country &&
          country !== "__default__" &&
          !translationCountries.includes(country)
        ) {
          stagesSrc = stagesSrc.filter((s) => {
            if (s.label === "Translations") {
              droppedDays += Math.max(1, s.days || 1);
              return false;
            }
            return true;
          });
        }
        const phaseDays = Math.max(1, pd.value - droppedDays);
        cEnd = addWorkingDays(cStart, phaseDays, set);
        if (stagesSrc.length) {
          let sc = cStart;
          cStages = stagesSrc.map((s) => {
            const sStart = nextBusinessDay(sc, set);
            const sEnd = addWorkingDays(sc, Math.max(1, s.days || 1), set);
            sc = nextBusinessDay(addDays(sEnd, 1), set);
            return { ...s, start: sStart, end: sEnd };
          });
        }
      } else {
        cEnd =
          pd.unit === "months"
            ? addMonths(cStart, pd.value)
            : addWeeks(cStart, pd.value);
      }
      countryCursor.set(country, nextBusinessDay(addDays(cEnd, 1), set));
      return {
        country: country === "__default__" ? null : country,
        kickoff: cKickoff,
        start: cStart,
        end: cEnd,
        stages: cStages,
      };
    });
    rows.push({
      name: pd.phase_name,
      durationText:
        pd.unit === "working days"
          ? `${pd.value} working days`
          : phaseDurText(pd),
      unit: pd.unit,
      from: tl?.from,
      countryRows,
    });
  }
  if (!rows.length && assumed) {
    const countryRows = effectiveCountries.map((country) => {
      const set =
        (countrySets || []).find((x) => x.country === country)?.set ||
        holidaySet;
      const cStart = snappedStart(country);
      const cEnd =
        assumed.unit === "months"
          ? addMonths(cStart, assumed.value)
          : addWeeks(cStart, assumed.value);
      return {
        country: country === "__default__" ? null : country,
        kickoff: cStart,
        start: cStart,
        end: cEnd,
        stages: [],
      };
    });
    rows = [
      {
        name: "Assumed live period",
        durationText: durationLabel(assumed),
        unit: assumed.unit,
        countryRows,
      },
    ];
  }
  if (!rows.length) return null;
  // Completion = latest country-row end across every phase. Sequential
  // mode (each country chains its own phases) means the latest row's
  // latest country is the project's true completion date.
  let completion = null;
  rows.forEach((r) =>
    (r.countryRows || []).forEach((cr) => {
      if (!completion || cr.end > completion) completion = cr.end;
    })
  );
  if (!completion) return null;
  return {
    rows,
    kickoff: earliestStart,
    completion,
    axisStart: addDays(earliestStart, -2),
    axisEnd: addDays(completion, 3),
  };
}

function GanttChart({ schedule, holidaySet }) {
  const { rows, axisStart, axisEnd } = schedule;
  const span = Math.max(differenceInCalendarDays(axisEnd, axisStart), 1);
  const dayWidth = (1 / span) * 100;
  const pos = (dt) =>
    Math.min(
      Math.max((differenceInCalendarDays(dt, axisStart) / span) * 100, 0),
      100
    );
  const widthOf = (a, b) =>
    Math.max(((differenceInCalendarDays(b, a) + 1) / span) * 100, 0.8);

  // Week ticks on Mondays; thin labels when the span is long.
  const ticks = [];
  let t = axisStart;
  while (t.getDay() !== 1) t = addDays(t, 1);
  while (t <= axisEnd) {
    ticks.push(t);
    t = addDays(t, 7);
  }
  const labelEvery = ticks.length > 14 ? Math.ceil(ticks.length / 10) : 1;
  const monthSpan = span > 92;

  const today = startOfDay(new Date());
  const showToday = today >= axisStart && today <= axisEnd;

  // Weekend bands: one per Saturday in the visible window, spanning
  // Sat+Sun. Rendered as faint fills BEHIND the stage bars on every
  // row so between-stage gaps are self-explanatory. Easy to revert:
  // delete this block + the per-row render + the legend entry.
  const weekendBands = [];
  {
    let w = axisStart;
    while (w.getDay() !== 6) w = addDays(w, 1);
    while (w <= axisEnd) {
      weekendBands.push(w);
      w = addDays(w, 7);
    }
  }

  // Weekday public holidays inside the visible window, shaded bands
  // on every row plus a hoverable dot on the axis naming the holiday.
  const holidayMarks = [];
  if (holidaySet) {
    for (const [dateIso, entries] of holidaySet.entries()) {
      const dt = startOfDay(new Date(`${dateIso}T00:00:00`));
      if (dt >= axisStart && dt <= axisEnd && !isWeekend(dt)) {
        holidayMarks.push({
          dt,
          entries,
          title: entries
            .map((e) => `${e.name} (${e.country})`)
            .join(" · "),
        });
      }
    }
  }

  // Office-closure bands inside the visible window. Each year's
  // closure period (24 Dec lunchtime → first working day of new year,
  // including the weekends inside) is rendered as ONE contiguous
  // calendar band so it reads as a single block on the Gantt instead
  // of a stack of weekday slivers. 24 Dec contributes its afternoon
  // only (half-day shift on the start position).
  const closureBands = [];
  {
    const startYear = axisStart.getFullYear();
    const endYear = axisEnd.getFullYear();
    for (let y = startYear - 1; y <= endYear; y++) {
      // Band start: 24 Dec lunchtime (half-day offset).
      const bandStart = new Date(y, 11, 24);
      // Band end (exclusive): first working day of y+1. UK bank-
      // holiday substitution for New Year's Day:
      //   1 Jan is normally non-working; if 1 Jan lands on a weekend,
      //   the Mon substitute is also non-working, so the first
      //   working day is the Tue after.
      const newYearDay = new Date(y + 1, 0, 1);
      const dow = newYearDay.getDay(); // 0 Sun, 6 Sat
      let firstWorking;
      if (dow === 6) {
        // Sat 1 Jan → Mon 3 closed → first working = Tue 4
        firstWorking = new Date(y + 1, 0, 4);
      } else if (dow === 0) {
        // Sun 1 Jan → Mon 2 closed → first working = Tue 3
        firstWorking = new Date(y + 1, 0, 3);
      } else {
        // Weekday 1 Jan closed → first working = next non-weekend
        firstWorking = new Date(y + 1, 0, 2);
        while (firstWorking.getDay() === 6 || firstWorking.getDay() === 0) {
          firstWorking = addDays(firstWorking, 1);
        }
      }
      // Inclusive end day = day before first working
      const bandEnd = addDays(firstWorking, -1);
      // Clip to the visible window.
      if (bandEnd < axisStart || bandStart > axisEnd) continue;
      const clippedStart = bandStart < axisStart ? axisStart : bandStart;
      const clippedEnd = bandEnd > axisEnd ? axisEnd : bandEnd;
      // Half-day offset applies only when the actual start is the
      // 24 Dec lunchtime moment (not when axis-clipped earlier).
      const halfStart =
        clippedStart.getMonth() === 11 && clippedStart.getDate() === 24;
      closureBands.push({
        start: clippedStart,
        end: clippedEnd,
        halfStart,
        label: `Medicys office closure · ${format(
          bandStart,
          "d MMM"
        )} – ${format(bandEnd, "d MMM yyyy")}`,
      });
    }
  }

  const segPopover = (content, dates, days, colour, description) => (
    <PopoverContent className="w-72 text-sm" side="top">
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="inline-block w-3 h-3 rounded-sm shrink-0"
          style={{ background: colour }}
        />
        <p className="font-medium text-stone-900">{content}</p>
        {days != null && (
          <span className="ml-auto font-mono text-xs text-stone-600">
            {days}d
          </span>
        )}
      </div>
      <p className="text-xs text-stone-600">{dates}</p>
      {description && (
        <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">
          {description}
        </p>
      )}
    </PopoverContent>
  );

  return (
    <div
      data-testid="timeline-gantt"
      className="select-none overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0"
    >
      <div className="min-w-[640px] sm:min-w-0">
      {/* axis */}
      <div className="flex">
        <div className="w-44 shrink-0" />
        <div className="relative flex-1 h-8">
          {ticks.map((tk, i) => (
            <div
              key={i}
              className="absolute top-2 bottom-0"
              style={{ left: `${pos(tk)}%` }}
            >
              <div className="h-2 border-l border-stone-300" />
              {i % labelEvery === 0 && (
                <p className="text-[9px] text-stone-500 font-mono mt-0.5 -translate-x-1/2 whitespace-nowrap">
                  {format(tk, monthSpan ? "d MMM yy" : "d MMM")}
                </p>
              )}
            </div>
          ))}
          {holidayMarks.map((h, i) => (
            <div
              key={`hd${i}`}
              title={`${format(h.dt, "EEE d MMM")}: ${h.title}`}
              data-testid={`holiday-dot-${i}`}
              className="absolute top-3 w-2 h-2 rounded-full bg-rose-400 cursor-help -translate-x-1/2"
              style={{ left: `${pos(h.dt)}%` }}
            />
          ))}
          {showToday && (
            <p
              className="absolute top-0 text-[9px] font-mono text-red-500 -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${pos(today)}%` }}
            >
              Today
            </p>
          )}
        </div>
      </div>

      {/* phase rows, one per country, no overall track */}
      <div className="space-y-1 mt-1">
        {rows.map((r, ri) => {
          const phaseEarliest = (r.countryRows || []).reduce(
            (m, x) => (x.start < m ? x.start : m),
            (r.countryRows && r.countryRows[0]?.start) || null
          );
          // Single-phase proposals don't need a "phase name · N working
          // days" header, the section title already says "Timelines"
          // and the duration is repeated in the phase-durations table.
          // Multi-phase proposals keep the header so each phase block
          // is visually labelled and separated.
          const multiPhase = rows.length > 1;
          return (
          <Fragment key={ri}>
          {multiPhase && (
          <div
            className={
              "flex items-center pb-1 " +
              (ri > 0 ? "pt-3 mt-2 border-t border-stone-200" : "pt-1")
            }
            data-testid={`gantt-phase-header-${ri}`}
          >
            <div className="w-44 shrink-0 pr-3">
              <p
                className="text-xs font-semibold text-stone-900 truncate"
                title={r.name}
              >
                {r.name || `Phase ${ri + 1}`}
              </p>
            </div>
            <p className="text-[10px] font-mono text-stone-500 tracking-wide uppercase">
              {r.durationText}
            </p>
          </div>
          )}
          {(r.countryRows || []).map((cr, ci) => {
            const cMarks = holidayMarks.filter((h) =>
              h.entries.some((e) => e.country === cr.country)
            );
            const hasGap = phaseEarliest && cr.start > phaseEarliest;
            return (
              <div
                key={`c${ci}`}
                className="flex items-center"
                data-testid={`gantt-country-row-${ri}-${ci}`}
              >
                <div className="w-44 shrink-0 pr-3 pl-2">
                  {cr.country && (
                    <p
                      className="text-[11px] font-medium text-stone-700 truncate"
                      title={cr.country}
                    >
                      {cr.country}
                    </p>
                  )}
                  {hasGap && (
                    <p
                      className="text-[9px] text-stone-500 whitespace-nowrap font-medium"
                      data-testid={`gantt-country-start-${ri}-${ci}`}
                    >
                      starts {format(cr.start, "d MMM")}
                    </p>
                  )}
                  <p className="text-[9px] text-stone-400 whitespace-nowrap">
                    finishes {format(cr.end, "EEE d MMM")}
                  </p>
                </div>
                <div className="relative flex-1 h-7 rounded bg-stone-100/60">
                  {weekendBands.map((w, i) => (
                    <div
                      key={`we${i}`}
                      data-testid={`gantt-weekend-${ri}-${ci}-${i}`}
                      className="absolute top-0 bottom-0 bg-stone-300/35 pointer-events-none"
                      style={{
                        left: `${pos(w)}%`,
                        width: `${Math.min(dayWidth * 2, 100 - pos(w))}%`,
                      }}
                    />
                  ))}
                  {ticks.map((tk, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 border-l border-stone-200/60"
                      style={{ left: `${pos(tk)}%` }}
                    />
                  ))}
                  {hasGap && (
                    <div
                      className="absolute top-1 bottom-1 rounded-[2px] bg-stone-300/40 ring-1 ring-stone-200/70 pointer-events-none flex items-center justify-center overflow-hidden"
                      data-testid={`gantt-country-gap-${ri}-${ci}`}
                      title={`${cr.country || "Project"} starts ${format(
                        cr.start,
                        "EEE d MMM yyyy"
                      )}`}
                      style={{
                        left: `${pos(phaseEarliest)}%`,
                        width: `${Math.max(
                          (differenceInCalendarDays(cr.start, phaseEarliest) /
                            span) *
                            100,
                          0.4
                        )}%`,
                      }}
                    >
                      <span className="text-[8px] font-mono text-stone-500 italic tracking-wide whitespace-nowrap px-1">
                        not yet started
                      </span>
                    </div>
                  )}
                  {cMarks.map((h, i) => (
                    <Popover key={`chb${i}`}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label={`${h.entries
                            .map((e) => e.name)
                            .join(", ")} (${cr.country || ""})`}
                          data-testid={`gantt-country-holiday-${ri}-${ci}-${i}`}
                          title={`${format(h.dt, "EEE d MMM")}: ${h.entries
                            .map((e) => e.name)
                            .join(" · ")}`}
                          className="absolute top-0 bottom-0 bg-rose-300/60 hover:bg-rose-400/70 cursor-help transition-colors"
                          style={{
                            left: `${pos(h.dt)}%`,
                            width: `${dayWidth}%`,
                          }}
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-64 text-sm" side="top">
                        <p className="font-medium text-stone-900 mb-1">
                          {h.entries.map((e) => e.name).join(" · ")}
                        </p>
                        <p className="text-xs text-stone-600">
                          {format(h.dt, "EEEE d MMMM yyyy")}
                          {cr.country ? ` · ${cr.country}` : ""}
                        </p>
                      </PopoverContent>
                    </Popover>
                  ))}
                  {cr.stages.length ? (
                    cr.stages.map((s, si) => (
                      <Popover key={si}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid={`gantt-country-seg-${ri}-${ci}-${si}`}
                            aria-label={`${s.label}${
                              cr.country ? ` (${cr.country})` : ""
                            }: ${s.days} working days`}
                            className="absolute top-1 bottom-1 rounded-[2px] ring-1 ring-white/40 hover:brightness-110 hover:ring-2 hover:ring-[#3EA8DC] transition-[filter,box-shadow] cursor-pointer"
                            style={{
                              left: `${pos(s.start)}%`,
                              width: `${widthOf(s.start, s.end)}%`,
                              backgroundColor:
                                _STAGE_COLOURS[s.label] || "#7C8DA1",
                            }}
                            title={`${s.label}${
                              cr.country ? ` · ${cr.country}` : ""
                            }: ${s.days} working days`}
                          />
                        </PopoverTrigger>
                        {segPopover(
                          `${s.label}${cr.country ? ` · ${cr.country}` : ""}`,
                          `${format(s.start, "EEE d MMM")} → ${format(
                            s.end,
                            "EEE d MMM yyyy"
                          )}`,
                          s.days,
                          _STAGE_COLOURS[s.label] || "#7C8DA1",
                          STAGE_DETAILS[s.label] || "Scheduled project activity."
                        )}
                      </Popover>
                    ))
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`gantt-country-bar-${ri}-${ci}`}
                          aria-label={`${cr.country || r.name}: ${
                            r.durationText
                          }`}
                          className="absolute top-1 bottom-1 rounded-[2px] bg-[#1B4F8A] ring-1 ring-white/40 hover:brightness-110 hover:ring-2 hover:ring-[#3EA8DC] transition-[filter,box-shadow] cursor-pointer"
                          style={{
                            left: `${pos(cr.start)}%`,
                            width: `${widthOf(cr.start, cr.end)}%`,
                          }}
                          title={`${cr.country || r.name}: ${format(
                            cr.start,
                            "d MMM"
                          )} → ${format(cr.end, "d MMM yyyy")}`}
                        />
                      </PopoverTrigger>
                      {segPopover(
                        cr.country || r.name || `Phase ${ri + 1}`,
                        `${format(cr.start, "EEE d MMM")} → ${format(
                          cr.end,
                          "EEE d MMM yyyy"
                        )}`,
                        null,
                        "#1B4F8A",
                        r.durationText
                      )}
                    </Popover>
                  )}
                  {closureBands.map((cb, i) => {
                    const startPos = pos(cb.start);
                    const w =
                      widthOf(cb.start, cb.end) -
                      (cb.halfStart ? dayWidth / 2 : 0);
                    const leftPos = startPos + (cb.halfStart ? dayWidth / 2 : 0);
                    return (
                      <Popover key={`closeband${i}`}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={cb.label}
                            data-testid={`gantt-closure-band-${ri}-${ci}-${i}`}
                            className="absolute top-0 bottom-0 cursor-help hover:brightness-95 transition-[filter]"
                            style={{
                              left: `${leftPos}%`,
                              width: `${w}%`,
                              backgroundColor: "rgba(71,85,105,0.30)",
                              backgroundImage:
                                "repeating-linear-gradient(45deg, rgba(15,23,42,0.55) 0 3px, transparent 3px 7px)",
                              borderLeft: "1px solid rgba(15,23,42,0.55)",
                              borderRight: "1px solid rgba(15,23,42,0.55)",
                            }}
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-64 text-sm" side="top">
                          <p className="font-medium text-stone-900 mb-1">
                            Medicys office closure
                          </p>
                          <p className="text-xs text-stone-600">
                            {format(cb.start, "EEE d MMM")} → {format(
                              cb.end,
                              "EEE d MMM yyyy"
                            )}
                          </p>
                          <p className="text-[11px] text-stone-500 mt-2">
                            Offices close from lunchtime on 24 December
                            until the first working day of the new year.
                            Working-day phases pause during this window.
                          </p>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                  {showToday && (
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400/60 pointer-events-none"
                      style={{ left: `${pos(today)}%` }}
                    />
                  )}
                </div>
              </div>
            );
          })}
          </Fragment>
          );
        })}
      </div>

      {rows.some((r) => (r.countryRows?.length || 0) >= 2) && (
        <p
          className="mt-2 pl-44 text-[10px] text-stone-500"
          data-testid="gantt-country-note"
        >
          One line per market; each follows its own bank-holiday calendar,
          so completion dates can differ.
        </p>
      )}

      {/* stage legend */}
      {(rows.some((r) =>
        (r.countryRows || []).some((cr) => cr.stages.length > 0)
      ) ||
        holidayMarks.length > 0 ||
        closureBands.length > 0) && (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 pl-44">
          {Object.entries(_STAGE_COLOURS)
            .filter(([label]) =>
              rows.some((r) =>
                (r.countryRows || []).some((cr) =>
                  cr.stages.some((s) => s.label === label)
                )
              )
            )
            .map(([label, colour]) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 text-[10px] text-stone-600"
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: colour }}
                />
                {label}
              </span>
            ))}
          {weekendBands.length > 0 && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] text-stone-600"
              data-testid="weekend-legend"
            >
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-stone-300/70" />
              Weekend (non-working)
            </span>
          )}
          {holidayMarks.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[10px] text-stone-600">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-300" />
              Public holiday (each country&apos;s own, hover for details)
            </span>
          )}
          {closureBands.length > 0 && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] text-stone-600"
              data-testid="closure-legend"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{
                  backgroundColor: "rgba(71,85,105,0.30)",
                  backgroundImage:
                    "repeating-linear-gradient(45deg, rgba(15,23,42,0.55) 0 2px, transparent 2px 5px)",
                }}
              />
              Medicys office closure (lunchtime 24 Dec → new year)
            </span>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1.5">
      {label}
    </p>
    <p className="text-2xl font-serif font-light text-stone-900 leading-none">
      {value}
    </p>
  </div>
);

// Stage palette tuned to the Medicys cobalt accent.
const _STAGE_COLOURS = {
  Setup: "#7C8DA1",
  Programming: "#3EA8DC",
  Translations: "#5BB8E5",
  Recruitment: "#1B4F8A",
  Fieldwork: "#2563EB",
  Moderation: "#0EA5E9",
  Deliverables: "#0B1220",
};

// One date picker per country, the first country is the project
// anchor (bordered/styled prominently), and changing it cascades to
// every other market. Other countries display compactly; clients can
// edit any one to pin a different start for that market.
function CountryStartPicker({ country, label, date, onChange, isAnchor }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`country-start-${country}`}
          className={
            "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors " +
            (isAnchor
              ? "border-2 border-[#1B4F8A] bg-white hover:bg-stone-50 text-stone-900 font-medium shadow-sm"
              : "border border-stone-300 bg-white hover:border-[#3EA8DC] hover:bg-stone-50 text-stone-700")
          }
        >
          <CalendarDays className="w-3.5 h-3.5 text-stone-500" />
          <span className="text-stone-500">{label}:</span>
          <span>{date ? fmtDay(date) : "-"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(dt) => {
            if (dt) onChange(dt);
            setOpen(false);
          }}
          disabled={{ before: new Date() }}
          defaultMonth={date || new Date()}
        />
      </PopoverContent>
    </Popover>
  );
}

